import React, { useEffect, useState } from "react";
import { Button } from "@xplatform/design-system";
import { IconSitemap } from "@tabler/icons-react";
import { useTranslation } from "react-i18next";
import { intelligenceEngineApi } from "@services/extractionApi";
import { mappingApi } from "@services/mappingApi";
import { useDoctypeMappingConfig } from "@hooks/extraction/useDoctypeMapping";
import OpenMappingControl from "./OpenMappingControl";

interface RunMappingControlProps {
  processId: string;
  doctypeCode?: string;
  /**
   * The per-run mapping opt-in (`process.mappingEnabled`). When true mapping
   * was already requested at upload time and {@link OpenMappingControl} owns
   * the header slot, so this control renders nothing.
   */
  mappingAlreadyEnabled: boolean;
  /** Gate: a result is loaded and its process id is available. */
  enabled: boolean;
  /** Batch results dispatch from their process header, not each item header. */
  showAction?: boolean;
  onSuccess: (message: string) => void;
  onError: (message: string) => void;
}

/**
 * "Run mapping" call-to-action shown to the left of Export for a completed
 * process whose run did NOT enable mapping, but whose doctype has mapping
 * configured. Clicking dispatches one process-wide mapping job on-demand;
 * the control then hands off to {@link OpenMappingControl} for live status.
 *
 * On mount it probes for an already-existing job (e.g. after a page refresh),
 * since the per-run `mappingEnabled` flag never flips for on-demand runs.
 * A batch has its action in the process header; its selected result only
 * shows this control's status, never another dispatch button.
 *
 * Renders nothing when mapping was already enabled for the run, the doctype
 * has no mapping config, or no result is loaded.
 */
const RunMappingControl: React.FC<RunMappingControlProps> = ({
  processId,
  doctypeCode,
  mappingAlreadyEnabled,
  enabled,
  showAction = true,
  onSuccess,
  onError,
}) => {
  const { t } = useTranslation();
  const mappingConfig = useDoctypeMappingConfig(doctypeCode);
  const mappingAvailable = Boolean(mappingConfig?.enabled);
  const eligible = enabled && mappingAvailable && !mappingAlreadyEnabled;

  const [dispatching, setDispatching] = useState(false);
  const [dispatched, setDispatched] = useState(false);
  // Until the existence probe resolves we render nothing rather than
  // flashing the CTA for a process that already has a job in flight.
  const [probing, setProbing] = useState(true);

  useEffect(() => {
    if (!eligible) {
      setProbing(false);
      return;
    }
    let cancelled = false;
    setProbing(true);
    setDispatched(mappingApi.wasDispatched(processId));
    const unsubscribe = mappingApi.onDispatch((dispatchedProcessId) => {
      if (dispatchedProcessId === processId) setDispatched(true);
    });
    mappingApi
      .getMappingJobByProcess(processId)
      .then((job) => {
        if (cancelled) return;
        if (job) {
          setDispatched(true);
          mappingApi.markDispatched(processId);
        }
      })
      .catch(() => {
        // Lookup failed — leave the on-demand action available.
      })
      .finally(() => {
        if (!cancelled) setProbing(false);
      });
    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [eligible, processId]);

  // Once dispatched (or a pre-existing job was found), defer entirely to the
  // status control: starting… → progress → Open mapping, like auto-mapping.
  if (dispatched) {
    return (
      <OpenMappingControl
        processId={processId}
        enabled={Boolean(processId)}
        showRetry={showAction}
      />
    );
  }

  if (!eligible || probing || !showAction) {
    return null;
  }

  const handleRun = async () => {
    setDispatching(true);
    try {
      await intelligenceEngineApi.dispatchMapping(processId);
      mappingApi.markDispatched(processId);
      onSuccess(t("extraction.mappingStatus.runStarted"));
    } catch (error) {
      console.error("Failed to dispatch mapping:", error);
      onError(t("extraction.mappingStatus.runFailed"));
      setDispatching(false);
    }
  };

  return (
    <Button
      size="xs"
      variant="filled"
      color="brand"
      leftSection={<IconSitemap size={16} />}
      loading={dispatching}
      onClick={handleRun}
      className="run-mapping-button"
    >
      {t("extraction.mappingStatus.runMapping")}
    </Button>
  );
};

export default RunMappingControl;
